import {
  useESSnackbar,
  ESButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  ESSelect,
} from '@energy-stacks/core/ui';
import { Dialog } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import {
  ChargingStationDetailsModel,
  TriggerMessageRequestOption21,
  triggerMessageRequestOptions21,
  useTriggerMessage21Mutation,
} from '@energy-stacks/broker/feature-charging-stations-data';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface TriggerChargingStationMessageDialogProps {
  isOpen: boolean;
  onClose: () => void;
  identityKey: ChargingStationDetailsModel['identityKey'];
}

export const TriggerChargingStationMessageDialogV21: React.FC<
  TriggerChargingStationMessageDialogProps
> = ({ identityKey, isOpen, onClose }) => {
  const { t } = useTranslation('chargingStations');
  const { showSnackbar } = useESSnackbar();
  const allowedMessages = [...triggerMessageRequestOptions21];

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
    getValues,
  } = useForm<{ requestedMessage: TriggerMessageRequestOption21 }>({
    resolver: yupResolver(
      yup.object().shape({
        requestedMessage: yup.string().required().oneOf(allowedMessages),
      })
    ),
    mode: 'onChange',
    defaultValues: {
      requestedMessage: undefined,
    },
  });
  const [triggerMessage, { isLoading }] = useTriggerMessage21Mutation();
  const requestRef = useRef<ReturnType<typeof triggerMessage>>();

  const onSubmit = useCallback(() => {
    const triggerChargingStationMessage = triggerMessage({
      identityKey: identityKey,
      requestedMessage: getValues().requestedMessage,
    });

    requestRef.current = triggerChargingStationMessage;

    triggerChargingStationMessage
      .unwrap()
      .then((result) => {
        if (result.status === 'Rejected') {
          showSnackbar('error', t('triggerMessageRejectedErrorMessage'));
          return;
        }

        if (result.status === 'NotImplemented') {
          showSnackbar('error', t('triggerMessageNotImplementedMessage'));
          return;
        }

        if (result.status === 'Accepted') {
          showSnackbar('success', t('triggerMessageSuccessMessage'));
        }

        onClose();
      })
      .catch((error) => {
        if (error.data?.errorCode === 'RPC_TIMEOUT') {
          showSnackbar('error', t('rpcTimeoutError'));
          return;
        }
        if (error.data?.errorCode === 'RPC_ERROR') {
          showSnackbar('error', t('rpcError'));
          return;
        }

        if (error.name === 'AbortError') {
          return;
        }

        showSnackbar('error');
      });
  }, [identityKey, triggerMessage, getValues, onClose, t, showSnackbar]);

  const handleCancel = useCallback(() => {
    requestRef.current?.abort();
    reset();
    onClose();
  }, [onClose, reset]);

  return (
    <Dialog open={isOpen} onClose={handleCancel} fullWidth maxWidth="sm">
      <ESDialogTitle data-testid="triggerMessagetitle">
        {t('remoteTriggerMessage')}
      </ESDialogTitle>

      <ESDialogContent>
        <Controller
          control={control}
          name="requestedMessage"
          render={({ field: { value, onChange, onBlur } }) => (
            <ESSelect
              testId="triggerMessage"
              label={t('messageType')}
              options={allowedMessages.map((option) => ({
                value: option,
                label: t(`triggerMessageRequestOption201.${option}`),
              }))}
              onChange={onChange}
              onBlur={onBlur}
              value={value || ''}
              required
              disabled={isLoading}
              sx={{ mt: 2 }}
            />
          )}
        />
      </ESDialogContent>

      <ESDialogActions>
        <ESButton
          data-testid="triggerMessageCancelButton"
          variant="text"
          color="error"
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t('cancel')}
        </ESButton>

        <ESButton
          data-testid="triggerMessageTriggerButton"
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={!isValid || isLoading}
          loading={isLoading}
        >
          {t('remoteTriggerMessage')}
        </ESButton>
      </ESDialogActions>
    </Dialog>
  );
};
